import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';

import ScanDetail from './ScanDetail';
import MobileScanReport from './mobile/MobileScanReport';
import PreviousScans from './PreviousScans';
import AppWalkthrough from 'components/AppWalkthrough';
import { ReadingDataWithSubParameters } from '../../../types';
import {
  HealthReadingData,
  ParameterHistoryParams,
} from 'services/face-scan/type';
import { WALKTHROUGH_CHECK } from 'lib/constants';
import useWalkthrough from 'hooks/useWalkthrough';
import useAuthStore from 'store/auth';
import {
  useGetHealthReadingById,
  useGetInfiniteHealthReading,
  useGetScanParameterHistory,
} from 'services/face-scan/hooks';

import { PATH_ROUTES } from 'routes/path';
import Loader from 'components/Loader';
import StartScanButton from './StartScanButton';
import { LoadMoreButton } from 'components/LoadMoreButton';
import { formatScanData, getCategorizedScanData } from 'lib/health-readings';

const COOKIE_NAME = WALKTHROUGH_CHECK.FACE_SCAN_WITH_DETAILS;

export default function FaceScanReportPage() {
  const navigate = useNavigate();
  const { inView } = useInView();

  const { updateStep, checkWalk, walkthroughSteps } = useWalkthrough();
  const profileId = useAuthStore((store) => store.currentUserProfileId);

  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirectTo');
  const scanId = searchParams.get('scan_id');

  const [readings, setReadings] = useState<HealthReadingData[]>([]);
  const [showWalkthrough, setShowWalkthrough] = useState<boolean>(false);
  const [historyParams, setHistoryParams] = useState<ParameterHistoryParams>({
    type: 'last_7',
  });
  const [selectedReadingId, setSelectedReadingId] = useState<string>('');

  const {
    data: healthReadings,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    refetch,
    isRefetching,
    hasNextPage,
  } = useGetInfiniteHealthReading({ profileId, initialPageParam: 1 });

  const { refetch: refetchParamHistory, isFetching: isParameterFetching } =
    useGetScanParameterHistory({
      profileId,
      params: historyParams,
      enabled: Boolean(healthReadings?.length),
    });

  const { data: readingData, isFetching: isReadingFetching } =
    useGetHealthReadingById({
      readingId: selectedReadingId,
      enabled: !!healthReadings?.length && !!selectedReadingId,
    });

  useEffect(() => {
    if (healthReadings?.length) {
      setReadings(healthReadings);
      setSelectedReadingId(scanId ? scanId : healthReadings[0].reading_id);

      if (healthReadings?.length > 0) {
        const hasWalked = checkWalk(COOKIE_NAME);
        setShowWalkthrough(!hasWalked);
      }
    } else {
      setReadings([]);
    }
  }, [healthReadings, profileId]);

  useEffect(() => {
    if (!redirectTo && healthReadings && !healthReadings?.length) {
      navigate(PATH_ROUTES.START_FACE_SCAN);
    }
  }, [healthReadings, redirectTo]);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  useEffect(() => {
    if (redirectTo) navigate(PATH_ROUTES.START_FACE_SCAN);
  }, [redirectTo]);

  const refetchScanReports = () => {
    refetch();
    refetchParamHistory();
  };

  const scanData = useMemo(
    () => (selectedReadingId ? formatScanData(readingData, 0) : []),
    [readingData, selectedReadingId]
  );

  const categorizedScanData = useMemo(
    () =>
      getCategorizedScanData({ healthReadings: readingData, selectedIndex: 0 }),
    [readingData]
  );

  const onWalkthroughComplete = () => updateStep(COOKIE_NAME);

  const onScan = () => {
    navigate(PATH_ROUTES.START_FACE_SCAN);
  };

  const scrollViewElement = (
    <div>
      {hasNextPage && !isFetchingNextPage ? (
        <LoadMoreButton onClick={() => fetchNextPage()} />
      ) : isFetchingNextPage ? (
        <Loader className='border-t h-14' loaderSize='size-6' />
      ) : null}
    </div>
  );

  if (isLoading || isRefetching) {
    return <Loader />;
  }

  return (
    <>
      <AppWalkthrough
        steps={walkthroughSteps.faceScan}
        run={showWalkthrough}
        onComplete={onWalkthroughComplete}
      />

      <MobileScanReport
        readings={readings}
        setReadings={setReadings}
        onScan={onScan}
        data={categorizedScanData}
        scanData={scanData as ReadingDataWithSubParameters[]}
        historyParams={historyParams}
        refetchScanReports={refetchScanReports}
        setHistoryParams={setHistoryParams}
        scrollViewElement={scrollViewElement}
        isParameterFetching={isParameterFetching}
      />

      <div className='hidden grid-cols-9 gap-4 p-6 lg:gap-8 sm:grid'>
        <div className='col-span-3 space-y-4 xl:col-span-2'>
          <StartScanButton onScan={onScan} />

          <div className='space-y-3' id='previous-scans'>
            <PreviousScans
              readings={readings}
              setReadings={setReadings}
              selectedReadingId={selectedReadingId}
              setSelectedReadingId={setSelectedReadingId}
              scrollViewElement={scrollViewElement}
            />
          </div>
        </div>

        <div className='h-full col-span-6 xl:col-span-7'>
          <ScanDetail
            data={categorizedScanData}
            scanData={scanData as ReadingDataWithSubParameters[]}
            className='h-[calc(100vh-17rem)] lg:h-[calc(100vh-15.5rem)]'
            readingId={selectedReadingId}
            refetchScanReports={refetchScanReports}
            setHistoryParams={setHistoryParams}
            historyParams={historyParams}
            isLoading={isReadingFetching || isLoading}
            isParameterFetching={isParameterFetching}
          />
        </div>
      </div>
    </>
  );
}
